'use client';

import { useState, type ReactNode } from 'react';
import {
  ArrowRight,
  Clock,
  Mail,
  MessageCircle,
  type LucideIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { useTranslations } from '@/hooks/useTranslations';
import { sendContactMessage } from '@/actions/contact/send-contact-message';

type T = (key: string) => string;

const SUPPORT_EMAIL = 'info@invoice-easy.com';

const SUBJECT_OPTIONS = [
  { value: 'general', labelKey: 'subjectGeneral' },
  { value: 'support', labelKey: 'subjectSupport' },
  { value: 'billing', labelKey: 'subjectBilling' },
  { value: 'feature', labelKey: 'subjectFeature' },
  { value: 'partnership', labelKey: 'subjectPartnership' },
] as const;

const FIELD_CLASS =
  'w-full bg-surface-mid border border-border/60 rounded-xl text-foreground px-4 py-3 text-sm placeholder:text-muted-foreground outline-none ring-0 focus:ring-0 focus:outline-none focus:border-brand focus:shadow-[0_0_0_1px_hsl(var(--brand)/0.4)] transition-[border-color,box-shadow] duration-300';

export function ContactPage() {
  const t = useTranslations('Contact');

  return (
    <div className="bg-background text-foreground">
      <main className="max-w-6xl mx-auto w-full px-6 md:px-12 pt-16 md:pt-24 pb-24 space-y-24">
        <HeroSection t={t} />
        <section className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-stretch">
          <div className="lg:col-span-7 flex">
            <ContactForm t={t} />
          </div>
          <div className="lg:col-span-5 flex flex-col gap-6">
            <InfoCard icon={Mail} title={t('contactUs')}>
              <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
                {t('directSupportDesc')}
              </p>
              <a
                href={`mailto:${SUPPORT_EMAIL}`}
                className="inline-flex items-center gap-2 text-brand hover:text-brand-hover transition-colors font-medium text-sm"
              >
                {SUPPORT_EMAIL}
                <ArrowRight className="w-4 h-4" />
              </a>
            </InfoCard>
            <InfoCard icon={Clock} title={t('supportHoursTitle')}>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {t('supportHoursDesc')}
              </p>
              <div className="mt-4 flex items-center gap-3">
                <span className="w-2 h-2 rounded-full bg-brand animate-pulse" />
                <span className="font-medium text-sm">
                  {t('supportHoursTime')}
                </span>
              </div>
              <p className="text-xs text-muted-foreground mt-1 ml-5">
                {t('supportHoursDays')}
              </p>
            </InfoCard>
          </div>
        </section>
      </main>
    </div>
  );
}

function HeroSection({ t }: { t: T }) {
  const [pre, accent] = splitOnDash(t('heroTitle'));

  return (
    <section className="text-center space-y-8 pt-6">
      <div className="inline-flex items-center justify-center gap-2 px-4 py-1.5 rounded-full bg-surface-high ghost-border text-brand font-medium text-sm primary-glow">
        <MessageCircle className="w-4 h-4" />
        {t('heroBadge')}
      </div>
      <h1 className="font-bold text-5xl md:text-7xl tracking-[-0.03em] leading-tight">
        {pre}
        {accent && (
          <>
            <br />
            <span className="text-transparent bg-clip-text bg-linear-to-r from-brand to-brand-hover">
              {accent}
            </span>
          </>
        )}
      </h1>
      <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto font-medium leading-relaxed">
        {t('heroSubtitle')}
      </p>
    </section>
  );
}

function ContactForm({ t }: { t: T }) {
  const [status, setStatus] = useState<
    'idle' | 'loading' | 'success' | 'error'
  >('idle');
  const [errorMessage, setErrorMessage] = useState('');

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setStatus('loading');
    setErrorMessage('');

    const form = e.currentTarget;
    const data = Object.fromEntries(new FormData(form)) as Record<
      string,
      string
    >;

    const result = await sendContactMessage({
      name: data.name,
      email: data.email,
      subject: data.subject,
      message: data.message,
      website: data.website,
    });

    if (result.error) {
      setStatus('error');
      setErrorMessage(result.error);
    } else {
      setStatus('success');
      form.reset();
    }
  }

  return (
    <div className="relative overflow-hidden rounded-xl bg-surface-high ghost-border p-6 md:p-10 w-full">
      <div className="absolute -top-24 -right-24 w-60 h-60 bg-brand/10 rounded-full blur-3xl pointer-events-none" />
      <div className="relative z-10">
        <h2 className="text-2xl font-bold tracking-tight mb-1">
          {t('formTitle')}
        </h2>
        <p className="text-muted-foreground text-sm mb-8">
          {t('formSubtitle')}
        </p>
        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <LabeledField id="name" label={t('formName')}>
              <input
                id="name"
                name="name"
                type="text"
                placeholder="Jane Doe"
                required
                className={FIELD_CLASS}
              />
            </LabeledField>
            <LabeledField id="email" label={t('formEmail')}>
              <input
                id="email"
                name="email"
                type="email"
                placeholder="jane@company.com"
                required
                className={FIELD_CLASS}
              />
            </LabeledField>
          </div>

          <LabeledField id="subject" label={t('formSubject')}>
            <select
              id="subject"
              name="subject"
              className={`${FIELD_CLASS} appearance-none`}
            >
              {SUBJECT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {t(opt.labelKey)}
                </option>
              ))}
            </select>
          </LabeledField>

          <LabeledField id="message" label={t('formMessage')}>
            <textarea
              id="message"
              name="message"
              rows={5}
              required
              placeholder={t('formMessagePlaceholder')}
              className={`${FIELD_CLASS} resize-none`}
            />
          </LabeledField>

          <Honeypot />

          {status === 'success' && (
            <p className="text-sm text-brand font-medium">{t('formSuccess')}</p>
          )}
          {status === 'error' && (
            <p className="text-sm text-error font-medium">{errorMessage}</p>
          )}

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-2">
            <Button
              type="submit"
              variant="brand"
              size="lg"
              disabled={status === 'loading'}
              className="px-8 py-6 text-base font-bold rounded-xl primary-glow"
            >
              {status === 'loading' ? t('formSending') : t('formSubmit')}
            </Button>
            <p className="text-xs text-muted-foreground italic">
              {t('responseNote')}
            </p>
          </div>
        </form>
      </div>
    </div>
  );
}

function InfoCard({
  icon: Icon,
  title,
  children,
}: {
  icon: LucideIcon;
  title: string;
  children: ReactNode;
}) {
  return (
    <div className="flex-1 rounded-xl bg-card ghost-border p-6 md:p-8">
      <div className="w-12 h-12 bg-surface-bright rounded-xl flex items-center justify-center mb-5 ghost-border">
        <Icon className="w-5 h-5 text-brand" />
      </div>
      <h3 className="text-lg font-bold mb-2">{title}</h3>
      {children}
    </div>
  );
}

function LabeledField({
  id,
  label,
  children,
}: {
  id: string;
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id} className="text-muted-foreground">
        {label}
      </Label>
      {children}
    </div>
  );
}

function Honeypot() {
  return (
    <div
      className="absolute opacity-0 top-0 left-0 h-0 w-0 -z-10"
      aria-hidden="true"
      tabIndex={-1}
    >
      <Label htmlFor="website">Website</Label>
      <input id="website" name="website" tabIndex={-1} autoComplete="off" />
    </div>
  );
}

function splitOnDash(title: string): [string, string] {
  const idx = title.indexOf('—');
  if (idx === -1) return [title, ''];
  return [title.slice(0, idx).trim(), title.slice(idx + 1).trim()];
}
